import { ref, uploadBytesResumable, getDownloadURL } from '@firebase/storage';
import * as ImageManipulator from 'expo-image-manipulator';
import { storage } from './firebase';

/**
 * Upload targets. Photos get heavier compression and a larger max dimension;
 * avatars are small + square. Web skips the manipulator step because the
 * picker already returns a downscaled blob and expo-image-manipulator on web
 * is slow (canvas round-trip).
 */
export type UploadKind = 'photo' | 'avatar';

const KIND_CONFIG: Record<UploadKind, { maxDim: number; quality: number }> = {
  photo: { maxDim: 1200, quality: 0.75 },
  avatar: { maxDim: 512, quality: 0.8 },
};

/**
 * Client-side downscale + JPEG re-encode. Cuts typical 4–10MB camera photos
 * down to ~150–400KB before Storage upload — saves bandwidth, storage cost,
 * and Firestore-triggered thumbnail generation time.
 */
export async function compressImage(localUri: string, kind: UploadKind): Promise<string> {
  const { maxDim, quality } = KIND_CONFIG[kind];
  try {
    const result = await ImageManipulator.manipulateAsync(
      localUri,
      [{ resize: { width: maxDim } }],
      { compress: quality, format: ImageManipulator.SaveFormat.JPEG }
    );
    return result.uri;
  } catch {
    // If manipulation fails for any reason (corrupt asset, web edge case),
    // fall back to the raw URI — better to upload the original than to fail.
    return localUri;
  }
}

/**
 * Given a Firebase Storage path for an original image, predict the path the
 * Resize Images extension writes a resized WebP variant to. The extension
 * drops each variant next to the original with a `_<size>` suffix and a
 * `.webp` extension.
 *
 * Returns null if the path doesn't look like one we upload (i.e. we never
 * configured the extension to watch its prefix).
 */
function resizedPathFor(originalStoragePath: string | undefined, size: string): string | null {
  if (!originalStoragePath) return null;
  if (!/^(posts|avatars)\//.test(originalStoragePath)) return null;
  const lastDot = originalStoragePath.lastIndexOf('.');
  const base = lastDot === -1 ? originalStoragePath : originalStoragePath.slice(0, lastDot);
  return `${base}_${size}.webp`;
}

/**
 * Predicted path for the 400x400 WebP thumbnail (feed/card use).
 */
export function thumbnailPathFor(originalStoragePath: string | undefined): string | null {
  return resizedPathFor(originalStoragePath, '400x400');
}

/**
 * Predicted path for the 1600x1600 WebP display image (full-screen
 * slideshow use). See thumbnailPathFor for the naming rule.
 */
export function displayPathFor(originalStoragePath: string | undefined): string | null {
  return resizedPathFor(originalStoragePath, '1600x1600');
}

/**
 * Resolve a downloadable URL for a stored path. Use when you have the
 * thumbnail path from thumbnailPathFor() and need a URL to render.
 * Falls back to null if the thumbnail hasn't been generated yet (the resize
 * extension runs async after upload — call sites should fall back to the
 * full-resolution URL).
 */
export async function getStorageUrl(storagePath: string): Promise<string | null> {
  try {
    return await getDownloadURL(ref(storage, storagePath));
  } catch {
    return null;
  }
}

// Only these path shapes are accepted by storage.rules — keep the client
// path constructor aligned so a typo never silently hits the bucket and
// gets rejected mid-upload. The `.jpg` extension is enforced because
// uploadImage always re-encodes to JPEG (see contentType below).
const ALLOWED_PATHS = [
  /^posts\/[A-Za-z0-9_-]{1,64}\/[A-Za-z0-9_.-]{1,128}\.jpg$/,
  /^avatars\/[A-Za-z0-9_-]{1,64}\/[A-Za-z0-9_.-]{1,128}\.jpg$/,
  /^structures\/[A-Za-z0-9_-]{1,64}\/checklist\/[A-Za-z0-9_-]{1,64}\/[A-Za-z0-9_.-]{1,128}\.jpg$/,
];

/**
 * Upload an already-compressed blob to Firebase Storage. Split out of
 * uploadImage so the offline outbox can compress once at enqueue time,
 * persist the blob locally, and replay this same upload step later without
 * re-running the manipulator. Validates storagePath on every call —
 * including replays — so a stale/tampered queued item can't bypass the
 * allowlist.
 */
export async function uploadBlob(
  blob: Blob,
  storagePath: string,
  kind: UploadKind = 'photo'
): Promise<{ downloadUrl: string; storagePath: string }> {
  if (!ALLOWED_PATHS.some((re) => re.test(storagePath))) {
    throw new Error(`Invalid storage path: ${storagePath}`);
  }

  const storageRef = ref(storage, storagePath);
  const uploadTask = uploadBytesResumable(storageRef, blob, {
    contentType: 'image/jpeg',
    // Post/checklist photos live at unique paths (timestamped ids) and are
    // never rewritten, so let browsers cache them forever — cuts Storage
    // egress 30-50% across repeat sessions. Avatars overwrite a fixed path
    // (avatars/{uid}/avatar.jpg), so they must stay revalidatable.
    cacheControl: kind === 'photo' ? 'public,max-age=31536000,immutable' : 'public,max-age=300',
  });

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      null,
      (error) => reject(error),
      async () => {
        const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
        resolve({ downloadUrl, storagePath });
      }
    );
  });
}

/**
 * Upload an image from a local URI to Firebase Storage.
 * Compresses/resizes before upload based on `kind`.
 * Returns { downloadUrl, storagePath }.
 */
export async function uploadImage(
  localUri: string,
  storagePath: string,
  kind: UploadKind = 'photo'
): Promise<{ downloadUrl: string; storagePath: string }> {
  const uploadUri = await compressImage(localUri, kind);
  const response = await fetch(uploadUri);
  const blob = await response.blob();
  return uploadBlob(blob, storagePath, kind);
}
